import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, Chip,
  Divider, Avatar,
} from '@mui/material';
import {
  Stars as StarsIcon,
  AutoAwesome as SearchIcon,
  Gavel as GavelIcon,
  Chat as ChatIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';

const POPUP_KEY = 'itfplus_credits_popup_count';
const MAX_SHOWS = 2;

const CreditsPopup: React.FC = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) return;
    const count = parseInt(localStorage.getItem(POPUP_KEY) || '0', 10);
    if (count < MAX_SHOWS) {
      // Piccolo delay per non apparire subito al caricamento
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [isAuthenticated()]);

  const handleClose = () => {
    const count = parseInt(localStorage.getItem(POPUP_KEY) || '0', 10);
    localStorage.setItem(POPUP_KEY, String(count + 1));
    setOpen(false);
  };

  const handleGoToCredits = () => {
    handleClose();
    navigate('/credits');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>

        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1B2A4A 0%, #2d3f6b 60%, #3d5078 100%)',
          px: 4, pt: 4, pb: 3.5,
          position: 'relative',
        }}>
          <Button
            onClick={handleClose}
            sx={{
              position: 'absolute', top: 12, right: 12,
              minWidth: 0, p: 0.5, color: 'rgba(255,255,255,0.5)',
              '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,215,0,0.15)', border: '2px solid rgba(255,215,0,0.4)', width: 52, height: 52 }}>
              <StarsIcon sx={{ color: '#FFD700', fontSize: 28 }} />
            </Avatar>
            <Box>
              <Chip label="NUOVO" size="small" sx={{ bgcolor: '#FFD700', color: '#1B2A4A', fontWeight: 800, fontSize: 10, mb: 0.5 }} />
              <Typography variant="h5" fontWeight={800} color="white" sx={{ fontFamily: 'Syne, sans-serif', lineHeight: 1.1 }}>
                Sistema Crediti BOOKY
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
            Accedi a funzionalità AI avanzate con un sistema semplice e trasparente.
            Ogni giorno hai consultazioni gratuite. Finiscono? Usa i crediti.
          </Typography>
        </Box>

        {/* Servizi */}
        <Box sx={{ px: 4, py: 3 }}>

          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1.5, fontSize: 11 }}>
            COME FUNZIONA
          </Typography>

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* BOOKY Search */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              p: 2, borderRadius: 2, bgcolor: 'rgba(99,102,241,0.06)',
              border: '1px solid rgba(99,102,241,0.12)'
            }}>
              <Avatar sx={{ bgcolor: 'rgba(99,102,241,0.12)', color: '#6366F1', width: 40, height: 40 }}>
                <SearchIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700} fontSize={14}>BOOKY Search</Typography>
                <Typography variant="caption" color="text.secondary">Ricerca AI su sentenze</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Chip label="3 gratis/giorno" size="small" sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 700, fontSize: 11, mb: 0.5, display: 'block' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>poi 1 credito / uso</Typography>
              </Box>
            </Box>

            {/* BOOKY Tribunale */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              p: 2, borderRadius: 2, bgcolor: 'rgba(139,92,246,0.06)',
              border: '1px solid rgba(139,92,246,0.12)'
            }}>
              <Avatar sx={{ bgcolor: 'rgba(139,92,246,0.12)', color: '#8B5CF6', width: 40, height: 40 }}>
                <GavelIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700} fontSize={14}>BOOKY Tribunale</Typography>
                <Typography variant="caption" color="text.secondary">Analisi predittiva casi legali</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Chip label="3 gratis/giorno" size="small" sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 700, fontSize: 11, mb: 0.5, display: 'block' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>poi 3 crediti / analisi</Typography>
              </Box>
            </Box>

            {/* Chatbot */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              p: 2, borderRadius: 2, bgcolor: 'rgba(6,182,212,0.06)',
              border: '1px solid rgba(6,182,212,0.12)'
            }}>
              <Avatar sx={{ bgcolor: 'rgba(6,182,212,0.12)', color: '#06B6D4', width: 40, height: 40 }}>
                <ChatIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700} fontSize={14}>Chatbot AI</Typography>
                <Typography variant="caption" color="text.secondary">Assistente legale conversazionale</Typography>
              </Box>
              <Chip label="SEMPRE GRATUITO" size="small" sx={{ bgcolor: 'rgba(6,182,212,0.12)', color: '#0891B2', fontWeight: 800, fontSize: 11 }} />
            </Box>

          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Garanzie */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 3 }}>
            {[
              'I crediti non scadono mai',
              'Fattura PDF automatica via email',
              'Pagamento sicuro con Stripe',
              'Pacchetti da €5 — inizia con "Provami!!"',
            ].map((text) => (
              <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon sx={{ fontSize: 16, color: '#10B981', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">{text}</Typography>
              </Box>
            ))}
          </Box>

          {/* CTA */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              fullWidth
              endIcon={<ArrowIcon />}
              onClick={handleGoToCredits}
              sx={{
                bgcolor: '#6366F1', fontWeight: 700, borderRadius: 2,
                textTransform: 'none', py: 1.5, fontSize: 15,
                '&:hover': { bgcolor: '#4F46E5' }
              }}
            >
              Scopri i pacchetti
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                fontWeight: 600, borderRadius: 2, textTransform: 'none',
                py: 1.5, px: 3, borderColor: 'rgba(0,0,0,0.15)',
                color: 'text.secondary', flexShrink: 0,
                '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.02)' }
              }}
            >
              Chiudi
            </Button>
          </Box>

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
            Questo messaggio apparirà ancora una volta al prossimo accesso
          </Typography>

        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreditsPopup;
